import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import Button from '@/components/Button/Button';
import Spinner from '@/components/Common/Spinner';
import Modal from '@/components/Modal/Modal';
import Typo from '@/components/Typography/Typo';
import { IMPORT_STATE } from '@/entities/(integration)/integrations';
import {
  useDeleteYotpoMutation,
  useImportYotpoFeedbackMutation,
  type YotpoInfo,
  yotpoInfoQueryOptions,
  yotpoQueryKeys,
} from '@/entities/(integration)/yotpo';
import useCrossWindowState from '@/hooks/domain/useCrossWindowState';
import useModalState from '@/hooks/pure/useModalState';
import type { Schema } from '@/shared/api/generated';
import useFeedbackSourceStore from '@/stores/Feedback/FeedbackSourceStore';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { SOURCE_TYPE } from '@/types/feedbackConst';
import { getYotpoUrl } from '@/utils/integration';

import IntegrationImportModal from '../../components/IntegrationImportModal';
import { ProviderContainer } from '../../components/ProviderContainer';
import type { IntegrationItemProps } from '../../types';

export const YotpoItem = ({ isConnector }: IntegrationItemProps) => {
  const currWorkspace = useWorkspaceStore((state) => state.currWorkspace);
  const fetchFeedbackSources = useFeedbackSourceStore(
    (state) => state.fetchFeedbackSources,
  );

  const workspaceAlias = currWorkspace?.alias;
  if (!workspaceAlias) throw new Error('Workspace alias is required');

  const { data: workspaces = [] } = useQuery(
    yotpoInfoQueryOptions(workspaceAlias, { enabled: !isConnector }),
  );
  const { mutate: disconnectMutation } = useDeleteYotpoMutation(workspaceAlias);
  const { mutate: requestImportMutation } =
    useImportYotpoFeedbackMutation(workspaceAlias);

  const queryClient = useQueryClient();
  const queryKeys = yotpoQueryKeys.all(workspaceAlias);

  // OAuth redirect 감지
  const [redirect, setRedirect, removeKey] =
    useCrossWindowState<Schema<'YotpoInfo'> | null>('yotpo-redirect', null);

  const importModalState = useModalState<{
    workspace?: YotpoInfo;
    isNew: boolean;
  }>();

  // OAuth 동시 팝업 방지
  const popupRef = useRef<Window | null>(null);

  const handleConnect = () => {
    if (!currWorkspace) return;
    if (popupRef.current && !popupRef.current.closed) return;
    popupRef.current = window.open(getYotpoUrl(currWorkspace.alias));
  };

  const handleDisconnect = (info: YotpoInfo) => {
    disconnectMutation({ alias: info.alias });
  };

  const handleImportWorkspace = (
    workspace: YotpoInfo,
    dateRange: NullableDateRangeT,
  ) => {
    requestImportMutation({ alias: workspace.alias, dateRange });
    importModalState.close();
  };

  // OAuth 완료 후 redirect 처리
  useEffect(() => {
    if (isConnector) return;
    if (!currWorkspace) return;
    if (!redirect) return;

    setRedirect(null);
    removeKey();
    // React Query 캐시 invalidate로 최신 데이터 refetch (race condition 회피)
    queryClient.invalidateQueries({ queryKey: queryKeys });
    fetchFeedbackSources(currWorkspace.alias);
    importModalState.open({ modalState: { isNew: true } });
  }, [redirect, removeKey, setRedirect, currWorkspace, isConnector]);

  return (
    <>
      <ProviderContainer
        multiInfoEnabled={true}
        connected={workspaces.length > 0}
        source={SOURCE_TYPE.YOTPO}
        description="Import reviews as feedback into Syncly."
        connectedDisplay={(info) => info.store_name || 'Yotpo store'}
        infos={workspaces ?? []}
        onDisconnect={handleDisconnect}
        onConnectClick={handleConnect}
        onAddInfoClick={handleConnect}
        additionalActions={(workspace: YotpoInfo) =>
          workspace.state === IMPORT_STATE.IMPORTING ? (
            <div className="flex items-center gap-2">
              <Spinner />
              <Typo type="body2" className="text-primary">
                Importing
              </Typo>
            </div>
          ) : (
            <Button
              variant="outlined"
              onClick={() =>
                importModalState.open({
                  modalState: {
                    workspace,
                    isNew: false,
                  },
                })
              }
            >
              One-time import
            </Button>
          )
        }
      />
      <Modal
        status={importModalState.status}
        closeModal={importModalState.close}
        useBackgroundClose
      >
        {importModalState.modalState && (
          <IntegrationImportModal
            isNew={importModalState.modalState.isNew}
            title={'Import Reviews'}
            subTitle={'Yotpo'}
            subTitleList={
              importModalState.modalState.workspace?.store_name ||
              importModalState.modalState.workspace?.alias
            }
            historyList={importModalState.modalState.workspace?.history || []}
            onSubmitImport={(start_date, end_date) => {
              const workspace = importModalState.modalState?.workspace;
              if (!workspace) return;
              handleImportWorkspace(workspace, { start_date, end_date });
            }}
          />
        )}
      </Modal>
    </>
  );
};
